import { useEffect, useState } from 'react';

/** Breakpoints mirrored from src/index.css — keep the two in sync. */
export const BREAKPOINTS = {
  sm: 480,
  md: 768,
  lg: 1024,
} as const;

const getMatch = (query: string): boolean => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(query).matches;
};

/**
 * Subscribes to a media query and re-renders when it flips.
 * Reads synchronously on first render so the first paint is already correct.
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => getMatch(query));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);

    // Re-sync in case the viewport changed between render and effect
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

/** True below the mobile shell boundary (drawer nav, full-screen modals). */
export const useIsMobile = (): boolean =>
  useMediaQuery(`(max-width: ${BREAKPOINTS.md}px)`);

/** True on tablets and narrow desktops. */
export const useIsTablet = (): boolean =>
  useMediaQuery(`(max-width: ${BREAKPOINTS.lg}px)`);

/** True when the primary pointer cannot hover (touch devices). */
export const useIsTouch = (): boolean =>
  useMediaQuery('(hover: none) and (pointer: coarse)');
